import { type IntlShape } from 'react-intl'

import {
  validateArrayNotEmpty,
  validateEmail,
  validateMinMaxValue,
  validateMinValue,
  validatePhoneNumber,
  validateRequired,
  validateTrueOrMissing,
} from '../base-form-handling'

type ValidationResult = {
  errors?: string[] | string
  valid: boolean
}

type Validator<T> = (value: T) => ValidationResult

type MarketplacePartnerRegisterPageFormValidations = {
  accountType: Validator<string>
  annualRevenue: Validator<string>
  brands: Validator<string[]>
  brandsProviderType: Validator<string>
  categories: Validator<string[]>
  city: Validator<string>
  companyType: Validator<string[]>
  country: Validator<string>
  countryCode: Validator<string>
  customerServiceInRespectiveLanguage: Validator<boolean>
  description: Validator<string>
  email: Validator<string>
  estimatedRevenue: Validator<number>
  firstName: Validator<string>
  keyBrand: Validator<string>
  lastName: Validator<string>
  marketReadiness: Validator<string>
  markets: Validator<string[]>
  middlewarePartnerName: Validator<string>
  name: Validator<string>
  phoneNumber: Validator<string>
  postalCode: Validator<string>
  productsCount: Validator<number>
  retailMediaMarketing: Validator<boolean>
  revenueExpected: Validator<string>
  revenueShare: Validator<number>
  shopSystem: Validator<string>
  streetName: Validator<string>
  streetNumber: Validator<string>
  targetCountries: Validator<string[]>
  vatNumber: Validator<string>
  website: Validator<string>
}

const validResult: ValidationResult = {
  valid: true,
}

export const makeMarketplacePartnerRegisterPageFormValidations = ({
  intl,
  isSellerCentralFeatureEnabled,
}: {
  intl: IntlShape
  isSellerCentralFeatureEnabled: boolean
  /* eslint-disable-next-line max-lines-per-function -- complex form validator */
}): MarketplacePartnerRegisterPageFormValidations => {
  const missingError = intl.formatMessage({ id: 'form.error.input.missing' })
  const minMaxError = intl.formatMessage(
    { id: 'form.error.input.minMaxValue' },
    {
      max: 100,
      min: 1,
    },
  )

  const skipInSellerCentral = <T>(validator: Validator<T>): Validator<T> => {
    return (value: T): ValidationResult => {
      if (isSellerCentralFeatureEnabled) {
        return validResult
      }

      return validator(value)
    }
  }

  const skipInLegacy = <T>(validator: Validator<T>): Validator<T> => {
    return (value: T): ValidationResult => {
      if (!isSellerCentralFeatureEnabled) {
        return validResult
      }

      return validator(value)
    }
  }

  return {
    accountType: skipInLegacy((value: string): ValidationResult =>
      validateRequired(value, missingError),
    ),
    annualRevenue: skipInLegacy((value: string): ValidationResult =>
      validateRequired(value, missingError),
    ),
    brands: skipInSellerCentral((value: string[]): ValidationResult =>
      validateArrayNotEmpty(value, missingError),
    ),
    brandsProviderType: skipInSellerCentral((value: string): ValidationResult =>
      validateRequired(value, missingError),
    ),
    categories: (value: string[]): ValidationResult =>
      validateArrayNotEmpty(value, missingError),
    city: skipInSellerCentral((value: string): ValidationResult =>
      validateRequired(value, missingError),
    ),
    companyType: skipInSellerCentral((value: string[]): ValidationResult =>
      validateArrayNotEmpty(value, missingError),
    ),
    country: skipInSellerCentral((value: string): ValidationResult =>
      validateRequired(value, missingError),
    ),
    countryCode: skipInLegacy((value: string): ValidationResult =>
      validateRequired(value, missingError),
    ),
    customerServiceInRespectiveLanguage: skipInSellerCentral(
      (value: boolean): ValidationResult =>
        validateTrueOrMissing(value, missingError),
    ),
    description: (): ValidationResult => validResult,
    email: (value: string): ValidationResult =>
      validateEmail(value, intl.formatMessage({ id: 'error.email.invalid' })),
    estimatedRevenue: skipInSellerCentral((value: number): ValidationResult =>
      validateMinValue(value, 1, missingError),
    ),
    firstName: (value: string): ValidationResult =>
      validateRequired(value, missingError),
    keyBrand: skipInLegacy((value: string): ValidationResult =>
      validateRequired(value, missingError),
    ),
    lastName: (value: string): ValidationResult =>
      validateRequired(value, missingError),
    marketReadiness: skipInLegacy((value: string): ValidationResult =>
      validateRequired(value, missingError),
    ),
    markets: skipInSellerCentral((value: string[]): ValidationResult =>
      validateArrayNotEmpty(value, missingError),
    ),
    middlewarePartnerName: skipInSellerCentral(
      (value: string): ValidationResult =>
        validateRequired(value, missingError),
    ),
    name: (value: string): ValidationResult =>
      validateRequired(value, missingError),
    phoneNumber: (value: string): ValidationResult =>
      validatePhoneNumber(value, missingError),
    postalCode: skipInSellerCentral((value: string): ValidationResult =>
      validateRequired(value, missingError),
    ),
    productsCount: skipInSellerCentral((value: number): ValidationResult =>
      validateMinValue(value, 1, missingError),
    ),
    retailMediaMarketing: (): ValidationResult => validResult,
    revenueExpected: skipInLegacy((value: string): ValidationResult =>
      validateRequired(value, missingError),
    ),
    revenueShare: skipInSellerCentral((value: number): ValidationResult =>
      validateMinMaxValue({
        maxValue: 100,
        message: minMaxError,
        minValue: 1,
        value,
      }),
    ),
    shopSystem: skipInSellerCentral((value: string): ValidationResult =>
      validateRequired(value, missingError),
    ),
    streetName: skipInSellerCentral((value: string): ValidationResult =>
      validateRequired(value, missingError),
    ),
    streetNumber: skipInSellerCentral((value: string): ValidationResult =>
      validateRequired(value, missingError),
    ),
    targetCountries: skipInLegacy((value: string[]): ValidationResult =>
      validateArrayNotEmpty(value, missingError),
    ),
    vatNumber: (): ValidationResult => validResult,
    website: (value: string): ValidationResult =>
      validateRequired(value, missingError),
  }
}
